<script setup lang="ts">
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Cid, Id, Post } from '@@/types'
import SurfacePopupWithTip from '@@/vuecomponents/SurfacePopupWithTip.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import type { DragEvent, MirrorOptions } from '@shopify/draggable'
import { Sortable } from '@shopify/draggable'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    postBeingEditedCid: Cid | null
    currentSortedPosts: Post[]
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const emit = defineEmits<{
  (name: 'dragging', dragging: boolean): void
}>()

const { user } = storeToRefs(useSurfaceStore())
const { canIModerate, canIWrite } = storeToRefs(useSurfacePermissionsStore())
const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())

const draggableWrapper = ref<ComponentPublicInstance | undefined>()
const postCidBeingDragged = ref<Cid | null>(null)
const dragging = ref<boolean>(false)
const arrayDuringSorting = ref<Post[]>([])
let mutationObserver: MutationObserver

function shouldMatchApprovalColor(isAboveLine: boolean, isPublished?: boolean): boolean {
  if (isPublished) return false
  if (isAboveLine) return canIModerate.value
  return !canIModerate.value
}

function wasWrittenByCurrentUser(post: Post): boolean {
  return !!user.value.id && String(post.author_id) === String(user.value.id)
}
function isEditableByCurrentUser(post: Post): boolean {
  return canIModerate.value || (wasWrittenByCurrentUser(post) && canIWrite.value)
}

function reorder(postCid: Cid, list: Post[]): void {
  const postOrderInCids = list.map((p) => p.cid)
  const postOrderInIds = list.map((p) => p.id)
  const postIndex = postOrderInCids.indexOf(postCid)
  const earlierPostId = postIndex === 0 ? null : postOrderInIds[postIndex - 1]
  const laterPostId = postIndex === list.length - 1 ? null : postOrderInIds[postIndex + 1]
  const sortIndex = computePostIndexFromPost(null, {
    prevId: laterPostId === null ? null : `wish-${laterPostId}`,
    nextId: earlierPostId === null ? null : `wish-${earlierPostId}`,
  }).sortIndex
  useSurfacePostsStore().sortPost({
    postCid,
    sortIndex,
  })
}

let sortable: Sortable
function makeSortable(): void {
  if (draggableWrapper.value === undefined) return
  sortable = new Sortable(draggableWrapper.value.$el as HTMLElement, {
    draggable: '.wish-wrapper',
    handle: '.circle-marker, .wish-popup',
    distance: 2, // allow clicking on buttons and element on post.
    mirror: {
      constrainDimensions: true,
      yAxis: false,
    } as MirrorOptions,
  })

  sortable.on('drag:start', (e: DragEvent) => {
    const postCid = e.originalSource.dataset.postCid
    const post = props.currentSortedPosts.find((p) => p.cid === postCid)
    if (!post || !isEditableByCurrentUser(post) || !!props.postBeingEditedCid) {
      e.cancel()
      return
    }
    // Move the original source "copy" to the end so that the layout won't be messed up
    const originalNode = e.originalSource
    const parentNode = originalNode.parentNode
    parentNode?.appendChild(originalNode)
    postCidBeingDragged.value = e.originalSource.dataset.postCid || null
    arrayDuringSorting.value = [...draggableSortablePostArray.value]
    dragging.value = true
  })
  sortable.on('mirror:created', (e) => {
    const dragNode = e.mirror
    if (dragNode) {
      const rect = e.source.querySelector('.wish-popup')!.getBoundingClientRect()
      const middleX = rect.right + rect.width / 2
      // We use this to move the line and circle to the cursor
      // when user started dragging the post
      const xOffsetFix = e.sensorEvent.clientX - middleX
      dragNode.style.right = xOffsetFix + 'px'
      mutationObserver = new MutationObserver(() => {
        dragNode.style.right = xOffsetFix + 'px'
      })
      mutationObserver.observe(dragNode, { attributes: true, attributeFilter: ['style'] })
    }
  })
  sortable.on('sortable:sorted', (e) => {
    const copy = [...arrayDuringSorting.value]
    copy.splice(e.newIndex, 0, copy.splice(e.oldIndex, 1)[0])
    arrayDuringSorting.value = copy
  })
  sortable.on('drag:stop', () => {
    draggableSortablePostArray.value = arrayDuringSorting.value
    mutationObserver?.disconnect()
    dragging.value = false
  })
}

function startLeaveAnimation(el: HTMLDivElement): void {
  el.style.right = el.dataset.right ?? ''
  el.style.top = el.dataset.top ?? ''
  el.style.position = 'absolute'

  const grandPa = el.parentElement?.parentElement
  el.parentElement?.removeChild(el)
  grandPa?.appendChild(el)
}

const draggableSortablePostArray = computed<Post[]>({
  get() {
    return [...props.currentSortedPosts]
  },
  set(updated: Post[]) {
    if (postCidBeingDragged.value != null) {
      reorder(postCidBeingDragged.value, updated)
      postCidBeingDragged.value = null
    }
  },
})

watch(dragging, () => {
  emit('dragging', dragging.value)
})

watch(shouldDisableDraggingPost, () => {
  sortable?.destroy()
  if (!shouldDisableDraggingPost.value) {
    makeSortable()
  }
})

onMounted(() => {
  makeSortable()
})
</script>

<template>
  <transition-group
    ref="draggableWrapper"
    tag="div"
    class="wish-draggable-wrapper current-wishes"
    :disabled="!!postBeingEditedCid"
    enter-class="opacity-0"
    enter-active-class="transform-opacity ease-out duration-500"
    enter-to-class="opacity-100"
    leave-class="opacity-100"
    leave-active-class="transition-opacity ease-in duration-500"
    leave-to-class=" opacity-0"
    :duration="500"
    @before-leave="startLeaveAnimation"
  >
    <div
      v-for="(post, index) in draggableSortablePostArray"
      :key="post.cid"
      :data-post-cid="post.cid"
      :class="['wish-wrapper', index % 2 ? 'below-line' : 'above-line']"
    >
      <div class="wish-popup-marker">
        <div class="circle-marker" />
        <div class="drag-vertical-line" />
      </div>
      <SurfacePopupWithTip
        :class="['wish-popup', index % 2 ? 'below-line' : 'above-line']"
        :color="post.color"
        :should-match-approval-color="shouldMatchApprovalColor(index % 2 === 0, post.published)"
      >
        <surface-post
          :index="index"
          :post="post"
          :post-reaction-ids="reactionIdsByPostId[post.id]"
          :post-comment-ids="commentIdsByPostId[post.id]"
          :put-attachment-on-top="true"
          :is-last="index === currentSortedPosts.length - 1"
          :x-shadow="false"
        />
      </SurfacePopupWithTip>
    </div>
  </transition-group>
</template>
